import React, { useEffect, useState } from "react";
import { Clock, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const REASONS = {
  vacation: "On leave — no slots available",
  day_off: "Not available on this day",
  not_working: "Not working on this day",
  fully_booked: "Fully booked — try another day",
  beyond_horizon: "Bookings open closer to the date",
  past: "This date has passed",
};
const GROUPS = [["Morning", 0, 12], ["Afternoon", 12, 17], ["Evening", 17, 24]];
const dayLabel = (iso, i) => {
  if (i === 0) return "Today";
  if (i === 1) return "Tomorrow";
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("en-IN", { weekday: "short" });
};
const dayNum = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" });

// Real free 30-min slots from the availability engine (IST). Every booking, incl. 15-min, takes one cell.
export function SlotPicker({ lawyerId, duration, date, slot, onChange, refreshKey = 0 }) {
  const [days, setDays] = useState(null);
  const [day, setDay] = useState(null); // { slots, reason }

  useEffect(() => {
    let alive = true;
    api.get(`/lawyers/${lawyerId}/slots`).then((r) => {
      if (!alive) return;
      setDays(r.data.days);
      if (!date) {
        const first = r.data.days.find((d) => d.free_count > 0) || r.data.days[0];
        if (first) onChange(first.date, "");
      }
    }).catch(() => alive && setDays("error"));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lawyerId, refreshKey]);

  useEffect(() => {
    if (!date) return undefined;
    let alive = true;
    setDay(null);
    api.get(`/lawyers/${lawyerId}/slots`, { params: { date, duration } })
      .then((r) => { if (alive) setDay(r.data); })
      .catch(() => alive && setDay({ slots: [], reason: "unavailable" }));
    return () => { alive = false; };
  }, [lawyerId, date, duration, refreshKey]);

  return (
    <div data-testid="slot-picker">
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" data-testid="date-strip">
        {days === null && <div className="text-xs text-slate-400 py-3 inline-flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading availability…</div>}
        {days === "error" && <p className="text-xs text-rose-600 py-3" data-testid="slots-load-error">Couldn't load availability — please refresh and try again.</p>}
        {Array.isArray(days) && days.map((d, i) => {
          const active = d.date === date;
          const none = d.free_count === 0;
          return (
            <button key={d.date} type="button" onClick={() => onChange(d.date, "")} data-testid={`day-${d.date}`} data-free={d.free_count}
              className={`shrink-0 w-[68px] rounded-xl border px-2 py-2 text-center transition-colors ${active ? "bg-[#0A2540] border-[#0A2540] text-white" : none ? "bg-slate-50 border-slate-100 text-slate-400" : "bg-white border-slate-200 text-slate-700 hover:border-[#0A2540]"}`}>
              <p className="text-[10px] uppercase tracking-wider font-bold">{dayLabel(d.date, i)}</p>
              <p className="text-xs font-extrabold mt-0.5">{dayNum(d.date)}</p>
              <span className={`mt-1 inline-block h-1.5 w-1.5 rounded-full ${none ? "bg-slate-200" : active ? "bg-[#D4AF37]" : "bg-emerald-500"}`} />
            </button>
          );
        })}
      </div>

      <p className="mt-3 text-xs flex items-center gap-1 font-medium text-slate-600"><Clock size={12} /> Available slots · IST</p>
      {day === null && date && <div className="mt-3 text-xs text-slate-400 inline-flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Checking slots…</div>}
      {day && day.slots.length === 0 && (
        <p className="mt-3 text-sm text-slate-500 bg-slate-50 rounded-xl px-3 py-3" data-testid="slots-empty">{REASONS[day.reason] || "No slots available on this day"}</p>
      )}
      {day && day.slots.length > 0 && GROUPS.map(([label, from, to]) => {
        const items = day.slots.filter((s) => { const h = parseInt(s.time.slice(0, 2), 10); return h >= from && h < to; });
        if (!items.length) return null;
        return (
          <div key={label} className="mt-3" data-testid={`slot-group-${label.toLowerCase()}`}>
            <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-1.5">{label}</p>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
              {items.map((s) => (
                <button key={s.time} type="button" onClick={() => onChange(date, s.time)} data-testid={`slot-${s.time}`}
                  className={`px-2 py-2 rounded-lg text-xs font-bold border transition-all ${slot === s.time ? "bg-[#0A2540] text-[#D4AF37] border-[#0A2540]" : "bg-white border-slate-200 text-slate-700 hover:border-[#0A2540]"}`}>
                  {s.time}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function HoldCountdown({ expiresAt }) {
  const [left, setLeft] = useState(() => Math.max(0, Math.round((new Date(expiresAt) - Date.now()) / 1000)));
  useEffect(() => {
    const id = setInterval(() => setLeft(Math.max(0, Math.round((new Date(expiresAt) - Date.now()) / 1000))), 1000);
    return () => clearInterval(id);
  }, [expiresAt]);
  const mm = String(Math.floor(left / 60)).padStart(1, "0");
  const ss = String(left % 60).padStart(2, "0");
  return (
    <p className={`text-xs font-bold text-center rounded-lg px-2 py-1.5 ${left > 0 ? "bg-amber-50 text-amber-800" : "bg-rose-50 text-rose-700"}`} data-testid="hold-countdown" data-left={left}>
      {left > 0 ? `Slot held for you · ${mm}:${ss}` : "Hold expired — the slot may be taken; please re-select"}
    </p>
  );
}
